import { useAuth } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { AdsChatPage } from '@/features/agency';
import { formatDocumentTitle } from '@/lib/document-title';

interface AdsChatSearch {
  /** A new conversation about this salon (short code); ignored once `thread` is set. */
  salon?: string;
  /** The open conversation — kept in the URL so a reload (even mid-answer) reopens it. */
  thread?: string;
  /** Opened from the Google tab: Google starter questions (the AI itself reads both platforms). */
  platform?: 'google';
}

const searchValue = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' && value.length <= 100 ? value.trim() : undefined;

/** Every agency member may ask (the parent layout gates non-agency users; threads are private to their owner). */
export const Route = createFileRoute('/_auth/agency/ads-doctor/ask')({
  validateSearch: (search: Record<string, unknown>): AdsChatSearch => {
    const thread = searchValue(search.thread);
    const salon = searchValue(search.salon);
    const platform = search.platform === 'google' ? { platform: 'google' as const } : {};
    if (thread) return { thread, ...platform };
    return salon ? { salon, ...platform } : platform;
  },
  component: AdsChatRoute,
});

function AdsChatRoute() {
  const { salon, thread, platform } = Route.useSearch();
  const platformSearch = platform ? { platform } : {};
  const navigate = useNavigate({ from: Route.fullPath });
  // Keyed by the signed-in user: switching accounts drops the page, which stops reading any answer of the old one.
  const { userId } = useAuth();
  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Ask AI · Ads Doctor')}</title>
      </Helmet>
      <AdsChatPage
        key={userId ?? 'signed-out'}
        threadId={thread}
        salonShortCode={salon}
        platform={platform ?? 'meta'}
        onOpenThread={(threadId) => navigate({ search: { thread: threadId, ...platformSearch } })}
        onStartNew={(shortCode) => void navigate({ search: shortCode ? { salon: shortCode, ...platformSearch } : platformSearch })}
      />
    </>
  );
}
